import { useId, useRef, useState, type ReactNode } from 'react'
import { Alert, Button, Checkbox, ListBox, Modal, Radio, RadioGroup, Select, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { useStaffText } from '../ui/StaffText'
import { Notice } from './settingsKit'
import '../styles/branch.css'

/** The HeroUI pieces the branch, equipment, profile and queue screens share. */

export interface PickOption {
  value: string
  label: string
  /** Shown quietly at the option's right: a queue letter, a state. */
  note?: string
  disabled?: boolean
}

// '' is a value like any other here; a list box key may not be empty.
const keyOf = (value: string) => `o:${value}`
const valueOf = (key: unknown) => String(key).slice(2)

/** A HeroUI Select over string values; a value missing from the options shows the placeholder. */
export function Pick({
  label,
  labelledBy,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  size,
  className,
}: {
  /** Its accessible name; the field's caption when one stands beside it. */
  label?: string
  labelledBy?: string
  value: string
  onChange: (next: string) => void
  options: readonly PickOption[]
  placeholder?: string
  disabled?: boolean
  size?: 'sm'
  className?: string
}) {
  const { say } = useStaffText()
  const chosen = options.find((o) => o.value === value)
  const empty = placeholder ?? say('common.choose')
  return (
    <Select
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className={['setup-pick', size === 'sm' ? 'setup-pick--sm' : '', className ?? ''].filter(Boolean).join(' ')}
      placeholder={empty}
      isDisabled={disabled}
      value={chosen ? keyOf(chosen.value) : null}
      disabledKeys={options.filter((o) => o.disabled).map((o) => keyOf(o.value))}
      onChange={(key) => {
        if (key == null) return
        const next = valueOf(key)
        if (next !== value) onChange(next)
      }}
    >
      <Select.Trigger>
        <Select.Value>{() => chosen?.label ?? empty}</Select.Value>
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {options.map((o) => (
            <ListBox.Item key={keyOf(o.value)} id={keyOf(o.value)} textValue={o.label}>
              {o.label}
              {o.note && <span className="setup-pick__note">{o.note}</span>}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

/** A question asked in place (a HeroUI Alert): what is about to happen, then its buttons. */
export function AskBar({
  title,
  tone = 'warning',
  role = 'alertdialog',
  className,
  children,
  actions,
}: {
  title?: ReactNode
  tone?: 'warning' | 'default'
  role?: 'alertdialog' | 'alert' | 'status' | 'note'
  className?: string
  /** Lines under the title. */
  children?: ReactNode
  actions: ReactNode
}) {
  return (
    <Alert status={tone} role={role} className={className ? `setup-ask ${className}` : 'setup-ask'}>
      <Alert.Content className="setup-ask__text">
        {title && <Alert.Title>{title}</Alert.Title>}
        {children && <Alert.Description>{children}</Alert.Description>}
      </Alert.Content>
      <span className="setup-ask__acts">{actions}</span>
    </Alert>
  )
}

/** A HeroUI checkbox; its children are the label the box is named by. */
export function Tick({
  checked,
  onChange,
  disabled,
  className,
  children,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <Checkbox variant="secondary" className={className ? `setup-tick ${className}` : 'setup-tick'} isSelected={checked} isDisabled={disabled} onChange={onChange}>
      <Checkbox.Content>
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>
        <span className="setup-tick__label">{children}</span>
      </Checkbox.Content>
    </Checkbox>
  )
}

/** One of a few values, as a HeroUI toggle group (a radio group to assistive tech). */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string
  options: ReadonlyArray<readonly [T, string]>
  value: T
  onChange: (v: T) => void
  disabled?: boolean
}) {
  return (
    <ToggleButtonGroup
      className="setup-seg"
      aria-label={label}
      size="sm"
      selectionMode="single"
      disallowEmptySelection
      isDisabled={disabled}
      selectedKeys={[keyOf(value)]}
      onSelectionChange={(keys) => {
        const [next] = [...keys]
        if (next !== undefined) onChange(valueOf(next) as T)
      }}
    >
      {options.map(([v, text]) => (
        <ToggleButton key={keyOf(v)} id={keyOf(v)}>
          {text}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

/** One of a few values, each with its own words: a HeroUI RadioGroup. */
export function Choices<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string
  options: ReadonlyArray<{ value: T; label: string; hint?: string }>
  value: T
  onChange: (v: T) => void
  disabled?: boolean
}) {
  return (
    <RadioGroup className="setup-choices" aria-label={label} value={value} onChange={(v) => onChange(v as T)} isDisabled={disabled}>
      {options.map((o) => (
        <Radio key={o.value} value={o.value}>
          <Radio.Content>
            <Radio.Control>
              <Radio.Indicator />
            </Radio.Control>
            <span className="setup-choice__text">
              <strong title={o.hint}>{o.label}</strong>
            </span>
          </Radio.Content>
        </Radio>
      ))}
    </RadioGroup>
  )
}

/** Several of a few values, each a HeroUI toggle; none on means any. */
export function Toggles({
  label,
  options,
  value,
  onToggle,
  disabled,
}: {
  label: string
  options: ReadonlyArray<readonly [string, string]>
  value: readonly string[]
  onToggle: (v: string) => void
  disabled?: boolean
}) {
  return (
    <ToggleButtonGroup
      className="setup-toggles"
      aria-label={label}
      size="sm"
      isDetached
      selectionMode="multiple"
      isDisabled={disabled}
      selectedKeys={value.map(keyOf)}
      onSelectionChange={(keys) => {
        const next = new Set([...keys].map(valueOf))
        const changed = options.find(([v]) => next.has(v) !== value.includes(v))
        if (changed) onToggle(changed[0])
      }}
    >
      {options.map(([v, text]) => (
        <ToggleButton key={keyOf(v)} id={keyOf(v)}>
          {text}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

/**
 * A form in a HeroUI Modal: Enter submits, the refusal is said at its top, and
 * closing after typing asks first. Submits and typing stop at its edge.
 */
export function SetupModal({
  title,
  onClose,
  onSubmit,
  submitLabel,
  valid = true,
  busy = false,
  error,
  extra,
  hideSubmit = false,
  wide = false,
  children,
}: {
  title: string
  onClose: () => void
  onSubmit?: () => void
  submitLabel?: string
  valid?: boolean
  busy?: boolean
  error?: string
  /** The footer's left side: a destructive action or a short note. */
  extra?: ReactNode
  hideSubmit?: boolean
  wide?: boolean
  children: ReactNode
}) {
  const { say } = useStaffText()
  const formId = useId()
  const dirty = useRef(false)
  const [asking, setAsking] = useState(false)
  const submit = !hideSubmit && onSubmit && submitLabel
  const close = () => (dirty.current ? setAsking(true) : onClose())
  const touched = (e: { stopPropagation: () => void }) => {
    e.stopPropagation()
    dirty.current = true
  }

  return (
    <Modal.Backdrop isOpen onOpenChange={(open) => !open && close()}>
      <Modal.Container size={wide ? 'lg' : 'md'} scroll="inside">
        <Modal.Dialog>
          <Modal.CloseTrigger aria-label={say('ui.close')} />
          <Modal.Header>
            <Modal.Heading>{title}</Modal.Heading>
          </Modal.Header>
          <Modal.Body>
            <form
              id={formId}
              className="setup-modal__form"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (submit && valid && !busy) onSubmit()
              }}
              onInput={touched}
              onChange={touched}
            >
              {error && <Notice>{error}</Notice>}
              {children}
            </form>
          </Modal.Body>
          <Modal.Footer className="setup-modal__foot">
            {asking ? (
              <>
                <strong className="setup-modal__ask">{say('ui.discardAsk')}</strong>
                <Button variant="tertiary" size="sm" autoFocus onPress={() => setAsking(false)}>
                  {say('ui.keepEditing')}
                </Button>
                <Button variant="danger" size="sm" onPress={onClose}>
                  {say('ui.discard')}
                </Button>
              </>
            ) : (
              <>
                {extra && <span className="setup-modal__extra">{extra}</span>}
                <Button variant="tertiary" onPress={close}>
                  {submit ? say('common.cancel') : say('ui.close')}
                </Button>
                {submit && (
                  <Button type="submit" form={formId} variant="primary" isDisabled={!valid || busy}>
                    {busy ? say('common.saving') : submitLabel}
                  </Button>
                )}
              </>
            )}
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}
